'use client'

import { useState, useSyncExternalStore } from 'react'
import { usePathname } from 'next/navigation'
import { useLocale } from 'next-intl'
import { X } from 'lucide-react'
import {
  SUGGESTION_COPY,
  hasLocalePrefix,
  localizedHref,
  pickSuggestedLocale,
} from '@/lib/i18n/locale-suggestion'
import type { Locale } from '@/lib/i18n/config'

const DISMISS_KEY = 'duchy:locale-suggestion-dismissed'

// Browser-only reads via useSyncExternalStore: the server snapshot is null, so
// the server render and the hydration pass both render nothing (no mismatch),
// then the client snapshot takes over. Every snapshot is a primitive, so it's
// stable between renders. Nothing here changes without a re-render (pathname
// changes re-render us), so subscribe is a no-op.
const noopSubscribe = () => () => {}
const serverNull = () => null

function readDismissed(): boolean {
  try {
    return window.sessionStorage.getItem(DISMISS_KEY) != null
  } catch {
    return false // storage unavailable (some private modes) — still show
  }
}

/**
 * "Read in <your language> →" pill for non-English visitors on an English
 * (un-prefixed) page. We opt out of Chrome's automatic page translation (see
 * the notranslate meta in src/app/layout.tsx), so this points those readers at
 * our own /<locale>/ version instead.
 *
 * - Fixed-position, client-only: navigator.languages is read through
 *   useSyncExternalStore with a null server snapshot (no hydration mismatch),
 *   and the pill doesn't shift layout (no CLS).
 * - A plain <a> (full page load) to the /<locale> equivalent — no redirect, no
 *   cookie, no change to caching or crawling.
 * - Dismissal lives in sessionStorage only (cleared with the tab), keeping the
 *   public site cookie-light like LanguageSwitcher; storage access is guarded
 *   because it throws in some private modes.
 */
export function LocaleSuggestionBanner() {
  const pathname = usePathname() ?? '/'
  const currentLocale = useLocale() as Locale
  const [dismissedNow, setDismissedNow] = useState(false)
  const suggested = useSyncExternalStore(
    noopSubscribe,
    () => pickSuggestedLocale(navigator.languages ?? [navigator.language]),
    serverNull,
  )
  const dismissedEarlier = useSyncExternalStore(noopSubscribe, readDismissed, () => true)
  const search = useSyncExternalStore(noopSubscribe, () => window.location.search, () => '')

  const target =
    currentLocale === 'en' && !hasLocalePrefix(pathname) && !dismissedEarlier && !dismissedNow
      ? suggested
      : null
  if (!target) return null
  const copy = SUGGESTION_COPY[target]

  const dismiss = () => {
    try {
      window.sessionStorage.setItem(DISMISS_KEY, '1')
    } catch {
      // ignore — hide for this page anyway
    }
    setDismissedNow(true)
  }

  return (
    <div
      className="pointer-events-none fixed inset-x-0 bottom-5 z-40 flex justify-center px-20"
      data-screenshot-exclude
    >
      <div
        lang={target}
        dir={target === 'ar' ? 'rtl' : 'ltr'}
        className="pointer-events-auto flex max-w-full items-center gap-1 rounded-full border border-border bg-card/95 py-1 pl-4 pr-1 text-sm shadow-lg backdrop-blur"
      >
        <a
          href={localizedHref(pathname, search, target)}
          hrefLang={target}
          className="truncate font-medium text-brand-700 hover:underline dark:text-brand-400"
        >
          {copy.cta} <span aria-hidden="true">{target === 'ar' ? '←' : '→'}</span>
        </a>
        <button
          type="button"
          onClick={dismiss}
          aria-label={copy.dismiss}
          className="rounded-full p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  )
}
